export const tenantCapability = {
  organizationAdminister: 'core.organization.administer',
  invitationCreate: 'core.invitation.create',
  operationView: 'coordination.operation.view',
  operationCreate: 'coordination.operation.create',
  operationEdit: 'coordination.operation.edit',
  agendaView: 'coordination.agenda.view',
} as const;

export const invitationRoleOptions = [
  { key: 'viewer', name: 'Visualizador', detail: 'somente leitura' },
  { key: 'coordinator', name: 'Coordenador', detail: 'operações' },
  {
    key: 'organization_administrator',
    name: 'Administrador',
    detail: 'empresa, equipe e operações',
  },
] as const;

export type TenantCapability = (typeof tenantCapability)[keyof typeof tenantCapability];
export type InvitableTenantRole = (typeof invitationRoleOptions)[number]['key'];

export interface TenantRolePresentation {
  readonly key: 'organization_administrator' | 'coordinator' | 'viewer';
  readonly name: string;
}

export interface TenantAccess {
  readonly registrationRequired?: boolean;
  readonly registrationProgress?: {
    profileComplete: boolean;
    baseComplete: boolean;
    aircraftComplete: boolean;
  };
  readonly capabilities: readonly string[];
  readonly roles?: readonly TenantRolePresentation[];
}

export function hasTenantCapability(
  access: TenantAccess | undefined,
  capability: TenantCapability,
): boolean {
  return access?.capabilities.includes(capability) === true;
}

export function hasTenantFixedRole(access: TenantAccess | undefined): boolean {
  return (access?.roles?.length ?? 0) > 0;
}

export function tenantRoleLabel(access: TenantAccess | undefined): string {
  const names = access?.roles
    ?.map((role) => role.name.trim())
    .filter((name, index, allNames) => name.length > 0 && allNames.indexOf(name) === index);

  return names && names.length > 0 ? names.join(' · ') : 'Acesso autorizado pela organização';
}

export function tenantAccessMode(
  access: TenantAccess | undefined,
): 'manage' | 'coordinate' | 'read' {
  if (
    hasTenantCapability(access, tenantCapability.organizationAdminister) ||
    hasTenantCapability(access, tenantCapability.invitationCreate)
  ) {
    return 'manage';
  }
  if (
    hasTenantCapability(access, tenantCapability.operationCreate) ||
    hasTenantCapability(access, tenantCapability.operationEdit)
  ) {
    return 'coordinate';
  }
  return 'read';
}

export function defaultOperationalSurface(access: TenantAccess | undefined): 'agenda' | 'board' {
  return hasTenantCapability(access, tenantCapability.agendaView) ? 'agenda' : 'board';
}
